<template>
  <!-- Component is portal'ed to always be last in the body (ref: App.vue)  -->
  <portal v-if="isOpen" to="modal-target">
    <transition name="modal" appear>
      <div role="dialog" class="modal-mask" @click="outsideClick()">
        <div :class="['modal-wrapper', `modal-wrapper--position-${position}`]">
          <div
            :class="[
              'modal-container',
              `modal-container--border-radius-size-${borderRadiusSize}`,
              { 'modal-container--overflowY': overflowY },
            ]"
            @click.stop
          >
            <slot />
          </div>
        </div>
      </div>
    </transition>
  </portal>
</template>

<script>
import { Portal } from 'portal-vue'

export default {
  components: {
    Portal,
  },
  props: {
    // TODO: make required and remove default value
    // Every parent should handle the state of whether the modal is open or not.
    // However, no parent should concern themselves with how we open or close the modal (by fx. v-if),
    // only the state of whether it's open or not.
    // Every parent should also handle the close event (ref below)
    isOpen: {
      type: Boolean,
      required: false,
      default: true,
    },
    overflowY: {
      type: Boolean,
      required: false,
      default: true,
    },
    borderRadiusSize: {
      type: String,
      required: false,
      default: 'large',
    },
    position: {
      type: String,
      required: false,
      default: 'center',
    },
    closeOnOutsideClick: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  // TODO: Ensure every parent emits and handles the close event
  // We currently have a bug where seemingly all modal won't be able to be re-opened after closing,
  // as the onClose event is not being emitted and handled by wrapping components
  emits: ['close', 'submit'],
  created() {
    document.addEventListener('keyup', this.handleKeyUp)
  },
  unmounted() {
    document.removeEventListener('keyup', this.handleKeyUp)
  },
  methods: {
    close() {
      this.$emit('close')
    },

    submit() {
      this.$emit('submit')
    },

    outsideClick() {
      if (this.closeOnOutsideClick) {
        this.close()
      }
    },

    handleKeyUp(event) {
      if (event.keyCode === 27) {
        // ESC
        this.close()
      }
      if (event.keyCode === 13) {
        // ENTER
        this.submit()
      }
    },
  },
}
</script>

<style scoped lang="scss">
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  transition: all $transition-regular;
  overflow-x: hidden;
  z-index: 98;

  .modal-wrapper {
    display: flex;
    width: inherit;
    height: inherit;
    overflow-y: auto;

    .modal-container {
      background: $color-body;
      border-radius: $border-radius-3xl;
      box-shadow: 0 10px 20px 0px rgba(0, 0, 0, 0.2);
      position: relative;
      width: fit-content;
      max-width: initial;

      &--overflowY {
        overflow-y: auto;
      }

      &--border-radius-size-small {
        border-radius: $border-radius-xl;
      }
      &--border-radius-size-medium {
        border-radius: $border-radius-2xl;
      }
      &--border-radius-size-large {
        border-radius: $border-radius-3xl;
      }
    }

    &--position-center {
      justify-content: center;
      align-items: center;

      .modal-container {
        max-height: 95vh;
      }
    }

    &--position-right {
      justify-content: flex-end;
      overflow-x: hidden;

      .modal-container {
        height: 100vh;
        max-height: 100vh;
        border-radius: 0;
      }
    }
  }

  @media (max-width: $mobile-width-max) {
    .modal-wrapper {
      align-items: flex-end;

      .modal-container {
        border-radius: $border-radius-3xl $border-radius-3xl 0 0;
        margin: 0;
        height: unset;
        max-height: 95vh;
        width: 100vw;
      }
    }
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: background-color $transition-slow ease;
}
.modal-enter,
.modal-leave-to {
  background-color: rgba(0, 0, 0, 0);
  transition: background-color $transition-regular ease-out;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: all $transition-regular ease;
}
.modal-enter .modal-container,
.modal-leave-to .modal-container {
  opacity: 0;
  transform: scale(0.75);
  transition: transform $transition-fast ease, opacity $transition-fast ease;
}

.modal-enter .modal-wrapper--position-right .modal-container,
.modal-leave-to .modal-wrapper--position-right .modal-container {
  opacity: 0;
  transform: translateX(100px);
  transition: transform $transition-fast ease, opacity $transition-fast ease;
}

@media screen and (max-width: 767px) {
  .modal-enter .modal-container,
  .modal-leave-to .modal-container {
    transform: translateY(100px);
  }
}
</style>
